"use client";

import {
    ADD_SURVEY_QUESTION,
    ADD_SURVEY_QUESTION_OPTION,
    CREATE_SURVEY,
    DELETE_SURVEY_QUESTION,
    DELETE_SURVEY_QUESTION_IMAGE,
    DELETE_SURVEY_QUESTION_OPTION,
    UPDATE_SURVEY,
    UPDATE_SURVEY_QUESTION,
    UPLOAD_SURVEY_IMAGE,
} from "@/graphql/mutations/surveyMutations";
import { SEARCH_PLAYERS_QUERY } from "@/graphql/queries/playerQueries";
import { SURVEY_DETAIL_QUERY } from "@/graphql/queries/surveyQueries";
import { useMutation, useQuery } from "@apollo/client";
import {
    ArrowDown,
    ArrowUp,
    ImagePlus,
    Plus,
    Save,
    Search,
    Trash2,
    X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface SelectedUser {
    id: string;
    name: string;
    nickname?: string;
}

interface QuestionOption {
    id?: string;
    text: string;
    order: number;
}

interface PendingImage {
    file: File;
    previewUrl: string;
}

interface QuestionImage {
    id: string;
    imageUrl: string;
    order: number;
}

interface Question {
    id?: string;
    text: string;
    questionType: string;
    isRequired: boolean;
    order: number;
    videoUrl: string;
    conditionalOnId: string | null;
    conditionalValue: string;
    options: QuestionOption[];
    images: QuestionImage[];
}

const QUESTION_TYPES = [
    { value: "text_short", label: "Short Text" },
    { value: "text_long", label: "Long Text" },
    { value: "number", label: "Number" },
    { value: "multiple_choice", label: "Multiple Choice" },
    { value: "checkbox", label: "Checkbox" },
    { value: "rating", label: "Rating (1-5)" },
];

const TARGET_TYPES = [
    { value: "all", label: "All Users" },
    { value: "specific_users", label: "Specific Users" },
    { value: "gota", label: "GOTA Members" },
    { value: "stream_team", label: "Stream Team" },
    { value: "moderators", label: "Moderators" },
    { value: "subscribers", label: "Subscribers" },
];

function utcToLocalDatetimeString(utcDateString: string): string {
    const date = new Date(utcDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function localDatetimeStringToUtc(localDateTime: string): string | null {
    return localDateTime ? new Date(localDateTime).toISOString() : null;
}

export default function SurveyBuilderPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEditing = Boolean(id);

    // Survey metadata
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [vpReward, setVpReward] = useState(0);
    const [targetType, setTargetType] = useState("all");
    const [status, setStatus] = useState("draft");
    const [endDate, setEndDate] = useState("");
    const [videoUrl, setVideoUrl] = useState("");
    const [targetUserIds, setTargetUserIds] = useState<string[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
    const [userSearchQuery, setUserSearchQuery] = useState("");

    // Questions
    const [questions, setQuestions] = useState<Question[]>([]);
    const [pendingImages, setPendingImages] = useState<
        Record<number, PendingImage[]>
    >({});
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Load existing survey if editing
    const { data: surveyData, loading: surveyLoading } = useQuery(
        SURVEY_DETAIL_QUERY,
        {
            variables: { id },
            skip: !isEditing,
            fetchPolicy: "network-only",
        },
    );

    const [createSurvey] = useMutation(CREATE_SURVEY);
    const [updateSurvey] = useMutation(UPDATE_SURVEY);
    const [addQuestion] = useMutation(ADD_SURVEY_QUESTION);
    const [updateQuestion] = useMutation(UPDATE_SURVEY_QUESTION);
    const [deleteQuestion] = useMutation(DELETE_SURVEY_QUESTION);
    const [addOption] = useMutation(ADD_SURVEY_QUESTION_OPTION);
    const [deleteOption] = useMutation(DELETE_SURVEY_QUESTION_OPTION);
    const [uploadSurveyImage] = useMutation(UPLOAD_SURVEY_IMAGE);
    const [deleteSurveyQuestionImage] = useMutation(
        DELETE_SURVEY_QUESTION_IMAGE,
    );
    const imageInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
    const { data: searchResults, loading: searchLoading } = useQuery(
        SEARCH_PLAYERS_QUERY,
        {
            variables: { searchQuery: userSearchQuery, limit: 10 },
            skip: userSearchQuery.length < 2,
        },
    );

    // Populate form with existing data
    useEffect(() => {
        if (surveyData?.survey) {
            const s = surveyData.survey;
            setTitle(s.title);
            setDescription(s.description);
            setIsAnonymous(s.isAnonymous);
            setVpReward(s.vpReward);
            setTargetType(s.targetType);
            const targetUsers = s.targetUsers || [];
            setTargetUserIds(
                targetUsers.map((user: { userId: string }) => user.userId),
            );
            setSelectedUsers(
                targetUsers.map(
                    (user: {
                        userId: string;
                        name: string;
                        nickname?: string;
                    }) => ({
                        id: user.userId,
                        name: user.name,
                        nickname: user.nickname,
                    }),
                ),
            );
            setStatus(s.status);
            setEndDate(s.endDate ? utcToLocalDatetimeString(s.endDate) : "");
            setVideoUrl(s.videoUrl || "");
            setQuestions(
                (s.questions || []).map(
                    (q: {
                        id: string;
                        text: string;
                        questionType: string;
                        isRequired: boolean;
                        order: number;
                        videoUrl: string;
                        conditionalOnId: string | null;
                        conditionalValue: string;
                        options: { id: string; text: string; order: number }[];
                        images: {
                            id: string;
                            imageUrl: string;
                            order: number;
                        }[];
                    }) => ({
                        id: q.id,
                        text: q.text,
                        questionType: q.questionType,
                        isRequired: q.isRequired,
                        order: q.order,
                        videoUrl: q.videoUrl || "",
                        conditionalOnId: q.conditionalOnId,
                        conditionalValue: q.conditionalValue || "",
                        options: (q.options || []).map(
                            (o: {
                                id: string;
                                text: string;
                                order: number;
                            }) => ({
                                id: o.id,
                                text: o.text,
                                order: o.order,
                            }),
                        ),
                        images: (q.images || []).map(
                            (img: {
                                id: string;
                                imageUrl: string;
                                order: number;
                            }) => ({
                                id: img.id,
                                imageUrl: img.imageUrl,
                                order: img.order,
                            }),
                        ),
                    }),
                ),
            );
        }
    }, [surveyData]);

    const addUser = (user: {
        userId: string | null;
        name: string;
        nickname?: string;
    }) => {
        if (!user.userId || targetUserIds.includes(user.userId)) return;
        setTargetUserIds((prev) => [...prev, user.userId as string]);
        setSelectedUsers((prev) => [
            ...prev,
            {
                id: user.userId as string,
                name: user.name,
                nickname: user.nickname,
            },
        ]);
        setUserSearchQuery("");
    };

    const removeUser = (userId: string) => {
        setTargetUserIds((prev) => prev.filter((id) => id !== userId));
        setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
    };

    const addNewQuestion = () => {
        setQuestions((prev) => [
            ...prev,
            {
                text: "",
                questionType: "text_short",
                isRequired: true,
                order: prev.length + 1,
                videoUrl: "",
                conditionalOnId: null,
                conditionalValue: "",
                options: [],
                images: [],
            },
        ]);
    };

    const removeQuestion = (index: number) => {
        setQuestions((prev) => prev.filter((_, i) => i !== index));
    };

    const updateQuestionField = (
        index: number,
        field: keyof Question,
        value: string | boolean | number | null,
    ) => {
        setQuestions((prev) =>
            prev.map((q, i) => (i === index ? { ...q, [field]: value } : q)),
        );
    };

    const addOptionToQuestion = (questionIndex: number) => {
        setQuestions((prev) =>
            prev.map((q, i) =>
                i === questionIndex
                    ? {
                          ...q,
                          options: [
                              ...q.options,
                              {
                                  text: "",
                                  order: q.options.length + 1,
                              },
                          ],
                      }
                    : q,
            ),
        );
    };

    const updateOptionText = (
        questionIndex: number,
        optionIndex: number,
        text: string,
    ) => {
        setQuestions((prev) =>
            prev.map((q, i) =>
                i === questionIndex
                    ? {
                          ...q,
                          options: q.options.map((o, oi) =>
                              oi === optionIndex ? { ...o, text } : o,
                          ),
                      }
                    : q,
            ),
        );
    };

    const removeOption = (questionIndex: number, optionIndex: number) => {
        setQuestions((prev) =>
            prev.map((q, i) =>
                i === questionIndex
                    ? {
                          ...q,
                          options: q.options.filter(
                              (_, oi) => oi !== optionIndex,
                          ),
                      }
                    : q,
            ),
        );
    };

    const handleImageUpload = async (
        questionIndex: number,
        files: FileList | null,
    ) => {
        if (!files || files.length === 0) return;
        const question = questions[questionIndex];

        if (question.id) {
            // Question already saved — upload immediately
            for (const file of Array.from(files)) {
                await uploadSingleImage(questionIndex, question.id, file);
            }
        } else {
            // Question not yet saved — store as pending
            const newPending = Array.from(files).map((file) => ({
                file,
                previewUrl: URL.createObjectURL(file),
            }));
            setPendingImages((prev) => ({
                ...prev,
                [questionIndex]: [
                    ...(prev[questionIndex] || []),
                    ...newPending,
                ],
            }));
        }
    };

    const uploadSingleImage = async (
        questionIndex: number,
        questionId: string,
        file: File,
    ) => {
        try {
            const formData = new FormData();
            formData.append(
                "operations",
                JSON.stringify({
                    query: `mutation UploadSurveyImage($questionId: UUID) {
                        uploadSurveyImage(questionId: $questionId) {
                            imageUrl
                            imageId
                        }
                    }`,
                    variables: { questionId },
                }),
            );
            formData.append("map", JSON.stringify({ "0": ["variables.file"] }));
            formData.append("0", file);

            const apiUrl =
                import.meta.env.VITE_VANDERHEIM_API_URL ||
                "http://localhost:8001/graphql/";
            const res = await fetch(apiUrl, {
                method: "POST",
                body: formData,
                credentials: "include",
            });
            const json = await res.json();
            const imageUrl = json?.data?.uploadSurveyImage?.imageUrl;
            const imageId = json?.data?.uploadSurveyImage?.imageId;

            if (imageUrl && imageId) {
                setQuestions((prev) =>
                    prev.map((q, i) =>
                        i === questionIndex
                            ? {
                                  ...q,
                                  images: [
                                      ...q.images,
                                      {
                                          id: imageId,
                                          imageUrl,
                                          order: q.images.length + 1,
                                      },
                                  ],
                              }
                            : q,
                    ),
                );
            }
        } catch (err) {
            console.error("Image upload failed:", err);
        }
    };

    const removePendingImage = (questionIndex: number, imageIndex: number) => {
        setPendingImages((prev) => {
            const current = prev[questionIndex] || [];
            URL.revokeObjectURL(current[imageIndex].previewUrl);
            return {
                ...prev,
                [questionIndex]: current.filter((_, i) => i !== imageIndex),
            };
        });
    };

    const handleDeleteImage = async (
        questionIndex: number,
        imageId: string,
    ) => {
        try {
            await deleteSurveyQuestionImage({
                variables: { id: imageId },
            });
            setQuestions((prev) =>
                prev.map((q, i) =>
                    i === questionIndex
                        ? {
                              ...q,
                              images: q.images.filter(
                                  (img) => img.id !== imageId,
                              ),
                          }
                        : q,
                ),
            );
        } catch (err) {
            console.error("Image delete failed:", err);
        }
    };

    const moveQuestion = (index: number, direction: "up" | "down") => {
        setQuestions((prev) => {
            const newQuestions = [...prev];
            const swapIndex = direction === "up" ? index - 1 : index + 1;
            if (swapIndex < 0 || swapIndex >= newQuestions.length) return prev;
            [newQuestions[index], newQuestions[swapIndex]] = [
                newQuestions[swapIndex],
                newQuestions[index],
            ];
            return newQuestions.map((q, i) => ({ ...q, order: i + 1 }));
        });
    };

    const handleSave = async () => {
        if (!title.trim()) return;
        setSaving(true);

        try {
            let surveyId = id;
            const endDateISO = localDatetimeStringToUtc(endDate);

            if (isEditing) {
                await updateSurvey({
                    variables: {
                        id,
                        title,
                        description,
                        isAnonymous,
                        vpReward,
                        targetType,
                        status,
                        endDate: endDateISO,
                        videoUrl: videoUrl || null,
                        targetUserIds:
                            targetType === "specific_users"
                                ? targetUserIds
                                : null,
                    },
                });
            } else {
                const result = await createSurvey({
                    variables: {
                        title,
                        description,
                        isAnonymous,
                        vpReward,
                        targetType,
                        status,
                        endDate: endDateISO,
                        videoUrl: videoUrl || null,
                        targetUserIds:
                            targetType === "specific_users"
                                ? targetUserIds
                                : null,
                    },
                });
                if (result.errors?.length) {
                    throw new Error(result.errors[0].message);
                }
                surveyId = result.data?.createSurvey?.survey?.id;
                if (!surveyId) {
                    throw new Error("Failed to create survey");
                }
            }

            // Sync questions
            if (isEditing && surveyData?.survey?.questions) {
                // Delete removed questions
                const currentIds = new Set(
                    questions.filter((q) => q.id).map((q) => q.id),
                );
                for (const existing of surveyData.survey.questions) {
                    if (!currentIds.has(existing.id)) {
                        await deleteQuestion({
                            variables: { id: existing.id },
                        });
                    }
                }
            }

            // Pass 1: Create/update questions without conditional references
            // Track mapping from placeholder IDs to real IDs
            const idMap = new Map<string, string>();

            for (const q of questions) {
                if (q.id) {
                    idMap.set(q.id, q.id);
                    await updateQuestion({
                        variables: {
                            id: q.id,
                            text: q.text,
                            questionType: q.questionType,
                            isRequired: q.isRequired,
                            order: q.order,
                            videoUrl: q.videoUrl || null,
                            conditionalOnId: null,
                            conditionalValue: null,
                        },
                    });

                    // Sync options for existing questions
                    const existingQuestion =
                        surveyData?.survey?.questions?.find(
                            (eq: { id: string }) => eq.id === q.id,
                        );
                    if (existingQuestion) {
                        const currentOptionIds = new Set(
                            q.options.filter((o) => o.id).map((o) => o.id),
                        );
                        for (const eo of existingQuestion.options || []) {
                            if (!currentOptionIds.has(eo.id)) {
                                await deleteOption({
                                    variables: { id: eo.id },
                                });
                            }
                        }
                    }

                    // Add new options
                    for (const opt of q.options) {
                        if (!opt.id) {
                            await addOption({
                                variables: {
                                    questionId: q.id,
                                    text: opt.text,
                                    order: opt.order,
                                },
                            });
                        }
                    }
                } else {
                    const result = await addQuestion({
                        variables: {
                            surveyId,
                            text: q.text,
                            questionType: q.questionType,
                            isRequired: q.isRequired,
                            order: q.order,
                            videoUrl: q.videoUrl || null,
                            conditionalOnId: null,
                            conditionalValue: null,
                        },
                    });

                    if (result.errors?.length) {
                        throw new Error(result.errors[0].message);
                    }
                    const newQuestionId =
                        result.data?.addSurveyQuestion?.question?.id;
                    if (!newQuestionId) {
                        throw new Error("Failed to add question");
                    }

                    // Map placeholder to real ID
                    const placeholderKey = `new-${questions.indexOf(q)}`;
                    idMap.set(placeholderKey, newQuestionId);
                    idMap.set(newQuestionId, newQuestionId);

                    for (const opt of q.options) {
                        await addOption({
                            variables: {
                                questionId: newQuestionId,
                                text: opt.text,
                                order: opt.order,
                            },
                        });
                    }
                }
            }

            // Pass 2: Update conditional references using real IDs
            for (const q of questions) {
                if (!q.conditionalOnId) continue;

                const realConditionalId = idMap.get(q.conditionalOnId);
                if (!realConditionalId) continue;

                const realQuestionId =
                    q.id || idMap.get(`new-${questions.indexOf(q)}`);
                if (!realQuestionId) continue;

                await updateQuestion({
                    variables: {
                        id: realQuestionId,
                        conditionalOnId: realConditionalId,
                        conditionalValue: q.conditionalValue || null,
                    },
                });
            }

            // Pass 3: Upload pending images for newly-created questions
            for (const [indexStr, images] of Object.entries(pendingImages)) {
                const qIndex = Number(indexStr);
                if (images.length === 0) continue;
                const q = questions[qIndex];
                const questionId = q.id || idMap.get(`new-${qIndex}`);
                if (!questionId) continue;

                for (const pending of images) {
                    await uploadSingleImage(qIndex, questionId, pending.file);
                    URL.revokeObjectURL(pending.previewUrl);
                }
            }
            setPendingImages({});

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
            navigate("/longhouse/surveys");
        } catch (err) {
            console.error("Error saving survey:", err);
        } finally {
            setSaving(false);
        }
    };

    if (surveyLoading) {
        return (
            <div className="flex min-h-[200px] items-center justify-center">
                <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
            </div>
        );
    }

    const needsOptions = (type: string) =>
        type === "multiple_choice" || type === "checkbox";

    return (
        <div className="mx-auto mt-4 w-full space-y-6 px-4">
            {/* Page Header */}
            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                            {isEditing ? "Edit Survey" : "Create Survey"}
                        </h1>
                        <p className="mt-2 text-slate-600 dark:text-slate-400">
                            {isEditing
                                ? "Update your survey details and questions"
                                : "Build a new survey for your community"}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {saveSuccess && (
                            <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                Saved successfully!
                            </span>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={saving || !title.trim()}
                            className="bg-brand hover:bg-brand/90 flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-colors disabled:opacity-50"
                        >
                            <Save className="h-5 w-5" />
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Survey metadata */}
            <div className="space-y-4 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                    Survey Details
                </h2>

                <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Title *
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                        placeholder="Survey title"
                    />
                </div>

                <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Description (supports Markdown)
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                        placeholder="Describe the purpose of this survey..."
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Target Audience
                        </label>
                        <select
                            value={targetType}
                            onChange={(e) => setTargetType(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                        >
                            {TARGET_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Status
                        </label>
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                        >
                            <option value="draft">Draft</option>
                            <option value="active">Active (Published)</option>
                            <option value="closed">Closed</option>
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                            VP Reward
                        </label>
                        <input
                            type="number"
                            min={0}
                            value={vpReward}
                            onChange={(e) =>
                                setVpReward(parseInt(e.target.value) || 0)
                            }
                            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                            End Date (optional)
                        </label>
                        <input
                            type="datetime-local"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                        />
                    </div>
                </div>

                {/* User picker for specific users */}
                {targetType === "specific_users" && (
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Select Users
                        </label>
                        <div className="relative">
                            <div className="flex items-center gap-2">
                                <Search className="h-4 w-4 text-slate-400" />
                                <input
                                    type="text"
                                    value={userSearchQuery}
                                    onChange={(e) =>
                                        setUserSearchQuery(e.target.value)
                                    }
                                    className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                                    placeholder="Search users by name..."
                                />
                            </div>
                            {userSearchQuery.length >= 2 && (
                                <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-slate-300 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800">
                                    {searchLoading && (
                                        <div className="px-4 py-2 text-center text-sm text-slate-500 dark:text-slate-400">
                                            Searching...
                                        </div>
                                    )}
                                    {!searchLoading &&
                                        searchResults?.searchPlayers
                                            .filter(
                                                (p: {
                                                    userId: string | null;
                                                }) =>
                                                    p.userId &&
                                                    !targetUserIds.includes(
                                                        p.userId,
                                                    ),
                                            )
                                            .map(
                                                (p: {
                                                    userId: string | null;
                                                    name: string;
                                                    nickname?: string;
                                                }) => (
                                                    <button
                                                        key={p.userId}
                                                        type="button"
                                                        onClick={() =>
                                                            addUser(p)
                                                        }
                                                        className="w-full px-4 py-2 text-left text-sm text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-700"
                                                    >
                                                        {p.name}
                                                        {p.nickname &&
                                                            ` (${p.nickname})`}
                                                    </button>
                                                ),
                                            )}
                                    {!searchLoading &&
                                        searchResults?.searchPlayers.filter(
                                            (p: { userId: string | null }) =>
                                                p.userId &&
                                                !targetUserIds.includes(
                                                    p.userId,
                                                ),
                                        ).length === 0 && (
                                            <div className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">
                                                No matching users found
                                            </div>
                                        )}
                                </div>
                            )}
                        </div>
                        {selectedUsers.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {selectedUsers.map((user) => (
                                    <span
                                        key={user.id}
                                        className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-slate-800 dark:bg-slate-700 dark:text-slate-200"
                                    >
                                        {user.name}
                                        {user.nickname && ` (${user.nickname})`}
                                        <button
                                            type="button"
                                            onClick={() => removeUser(user.id)}
                                            className="ml-1 rounded-full p-0.5 hover:bg-slate-300 dark:hover:bg-slate-600"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                            YouTube Video URL (optional)
                        </label>
                        <input
                            type="url"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                            placeholder="https://www.youtube.com/watch?v=..."
                        />
                    </div>

                    <div className="flex items-end">
                        <label className="inline-flex cursor-pointer items-center gap-2">
                            <input
                                type="checkbox"
                                checked={isAnonymous}
                                onChange={(e) =>
                                    setIsAnonymous(e.target.checked)
                                }
                                className="h-4 w-4 rounded border-slate-300 bg-white text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300">
                                Anonymous responses (hide user names in results)
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Questions */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        Questions
                    </h2>
                    <button
                        onClick={addNewQuestion}
                        className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    >
                        <Plus className="h-4 w-4" />
                        Add Question
                    </button>
                </div>

                {questions.length === 0 && (
                    <div className="rounded-2xl border border-slate-300 bg-white p-8 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
                        <p className="text-slate-600 dark:text-slate-400">
                            No questions yet. Add your first question.
                        </p>
                    </div>
                )}

                {questions.map((q, qIndex) => (
                    <div
                        key={qIndex}
                        className="space-y-3 rounded-2xl border border-slate-300 bg-white p-5 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                    >
                        <div className="flex items-start justify-between">
                            <span className="mt-1 text-xs font-semibold text-slate-400 dark:text-slate-600">
                                Question {qIndex + 1}
                            </span>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => moveQuestion(qIndex, "up")}
                                    disabled={qIndex === 0}
                                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                                    title="Move up"
                                >
                                    <ArrowUp className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => moveQuestion(qIndex, "down")}
                                    disabled={qIndex === questions.length - 1}
                                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                                    title="Move down"
                                >
                                    <ArrowDown className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => removeQuestion(qIndex)}
                                    className="rounded p-1 text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
                                    title="Delete question"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <div className="md:col-span-2">
                                <label className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-400">
                                    Question Text
                                </label>
                                <textarea
                                    value={q.text}
                                    onChange={(e) =>
                                        updateQuestionField(
                                            qIndex,
                                            "text",
                                            e.target.value,
                                        )
                                    }
                                    rows={2}
                                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                                    placeholder="Enter your question..."
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-400">
                                    Type
                                </label>
                                <select
                                    value={q.questionType}
                                    onChange={(e) =>
                                        updateQuestionField(
                                            qIndex,
                                            "questionType",
                                            e.target.value,
                                        )
                                    }
                                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                                >
                                    {QUESTION_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>
                                            {t.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                            <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300">
                                <input
                                    type="checkbox"
                                    checked={q.isRequired}
                                    onChange={(e) =>
                                        updateQuestionField(
                                            qIndex,
                                            "isRequired",
                                            e.target.checked,
                                        )
                                    }
                                    className="h-3.5 w-3.5 rounded border-slate-300 bg-white text-blue-600 dark:border-slate-600 dark:bg-slate-800"
                                />
                                Required
                            </label>

                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                <label>Video URL:</label>
                                <input
                                    type="url"
                                    value={q.videoUrl}
                                    onChange={(e) =>
                                        updateQuestionField(
                                            qIndex,
                                            "videoUrl",
                                            e.target.value,
                                        )
                                    }
                                    className="w-48 rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                                    placeholder="YouTube URL"
                                />
                            </div>
                        </div>

                        {/* Conditional logic */}
                        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                            <label>Show only when question</label>
                            <select
                                value={q.conditionalOnId ?? ""}
                                onChange={(e) =>
                                    updateQuestionField(
                                        qIndex,
                                        "conditionalOnId",
                                        e.target.value || null,
                                    )
                                }
                                className="rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                            >
                                <option value="">Always show</option>
                                {questions
                                    .filter((_, i) => i !== qIndex)
                                    .map((cq, ci) => (
                                        <option
                                            key={ci}
                                            value={cq.id ?? `new-${ci}`}
                                        >
                                            Q{ci + 1}: {cq.text.slice(0, 30)}
                                        </option>
                                    ))}
                            </select>
                            {q.conditionalOnId && (
                                <>
                                    <label>equals</label>
                                    <input
                                        type="text"
                                        value={q.conditionalValue}
                                        onChange={(e) =>
                                            updateQuestionField(
                                                qIndex,
                                                "conditionalValue",
                                                e.target.value,
                                            )
                                        }
                                        className="w-32 rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                                        placeholder="value"
                                    />
                                </>
                            )}
                        </div>

                        {/* Options for MC/Checkbox */}
                        {needsOptions(q.questionType) && (
                            <div className="ml-4 space-y-2 border-l-2 border-slate-200 pl-4 dark:border-slate-700">
                                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                                    Options
                                </label>
                                {q.options.map((opt, oIndex) => (
                                    <div
                                        key={oIndex}
                                        className="flex items-center gap-2"
                                    >
                                        <span className="text-xs text-slate-400 dark:text-slate-600">
                                            {oIndex + 1}.
                                        </span>
                                        <input
                                            type="text"
                                            value={opt.text}
                                            onChange={(e) =>
                                                updateOptionText(
                                                    qIndex,
                                                    oIndex,
                                                    e.target.value,
                                                )
                                            }
                                            className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                                            placeholder={`Option ${oIndex + 1}`}
                                        />
                                        <button
                                            onClick={() =>
                                                removeOption(qIndex, oIndex)
                                            }
                                            className="rounded p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={() => addOptionToQuestion(qIndex)}
                                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                >
                                    <Plus className="h-3 w-3" />
                                    Add Option
                                </button>
                            </div>
                        )}

                        {/* Images */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                                    Images
                                </label>
                                <input
                                    ref={(el) => {
                                        imageInputRefs.current[qIndex] = el;
                                    }}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                        handleImageUpload(
                                            qIndex,
                                            e.target.files,
                                        );
                                        e.target.value = "";
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        imageInputRefs.current[qIndex]?.click()
                                    }
                                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                >
                                    <ImagePlus className="h-3.5 w-3.5" />
                                    Add Images
                                </button>
                            </div>
                            {(q.images.length > 0 ||
                                (pendingImages[qIndex]?.length ?? 0) > 0) && (
                                <div className="flex flex-wrap gap-3">
                                    {q.images.map((img) => (
                                        <div
                                            key={img.id}
                                            className="group relative"
                                        >
                                            <img
                                                src={img.imageUrl}
                                                alt=""
                                                className="h-24 w-24 rounded-lg border border-slate-300 object-cover dark:border-slate-600"
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleDeleteImage(
                                                        qIndex,
                                                        img.id,
                                                    )
                                                }
                                                className="absolute -top-1.5 -right-1.5 hidden rounded-full bg-red-600 p-0.5 text-white shadow-md group-hover:block hover:bg-red-700"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                    {pendingImages[qIndex]?.map(
                                        (pending, pIndex) => (
                                            <div
                                                key={`pending-${pIndex}`}
                                                className="group relative"
                                            >
                                                <img
                                                    src={pending.previewUrl}
                                                    alt=""
                                                    className="h-24 w-24 rounded-lg border-2 border-dashed border-blue-400 object-cover dark:border-blue-500"
                                                />
                                                <div className="pointer-events-none absolute inset-0 flex items-end justify-center rounded-lg bg-blue-500/10">
                                                    <span className="mb-1 rounded bg-blue-600/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                                        Pending
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removePendingImage(
                                                            qIndex,
                                                            pIndex,
                                                        )
                                                    }
                                                    className="absolute -top-1.5 -right-1.5 hidden rounded-full bg-red-600 p-0.5 text-white shadow-md group-hover:block hover:bg-red-700"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ),
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
